import React from "react";
import { Asset, AssetRateCard, ProjectSetup } from "../types";
import { X, Image as ImageIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../utils";
import { BID_ITEM_STATUSES, getBidStatusValue } from "../lib/bidStatus";

interface Props {
  asset: Asset;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (id: string, field: keyof Asset, value: any) => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  readOnly?: boolean;
  rates: AssetRateCard;
  setup: ProjectSetup;
}

export function AssetDetailModal({ asset, isOpen, onClose, onUpdate, onNavigate, hasPrev, hasNext, readOnly, rates, setup }: Props) {
  if (!isOpen) return null;

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, field: keyof Asset, currentValue: string) => {
    if (!onUpdate) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = e.target as HTMLTextAreaElement;
      const start = target.selectionStart;
      const end = target.selectionEnd;
      
      if (currentValue.length === 0) {
        const bullet = "• ";
        onUpdate(asset.id, field, bullet);
        setTimeout(() => {
          target.selectionStart = target.selectionEnd = bullet.length;
        }, 0);
        return;
      }

      const bullet = "\n• ";
      const newValue = currentValue.substring(0, start) + bullet + currentValue.substring(end);
      onUpdate(asset.id, field, newValue);
      
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = start + bullet.length;
      }, 0);
    }
  };

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly || !onUpdate) return;
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const targetWidth = 480;
          const targetHeight = 270;
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d");

          if (ctx) {
            const imgAspectRatio = img.width / img.height;
            const targetAspectRatio = targetWidth / targetHeight;
            let drawWidth, drawHeight, offsetX, offsetY;

            if (imgAspectRatio > targetAspectRatio) {
              drawHeight = targetHeight;
              drawWidth = img.width * (targetHeight / img.height);
              offsetX = (targetWidth - drawWidth) / 2;
              offsetY = 0;
            } else {
              drawWidth = targetWidth;
              drawHeight = img.height * (targetWidth / img.width);
              offsetX = 0;
              offsetY = (targetHeight - drawHeight) / 2;
            }

            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
            const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
            onUpdate(asset.id, "thumbnail", dataUrl);
          }
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">{asset.assetCode}</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Asset Name</p>
            </div>
            {onNavigate && (
              <div className="ml-4 flex items-center gap-1 border-l border-slate-200 pl-4 dark:border-slate-700">
                <button
                  onClick={() => onNavigate('prev')}
                  disabled={!hasPrev}
                  className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-slate-700 dark:hover:text-slate-200"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => onNavigate('next')}
                  disabled={!hasNext}
                  className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-slate-700 dark:hover:text-slate-200"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Large Thumbnail Section */}
            <div className="space-y-4">
              <div className={cn(
                "relative aspect-video rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden group dark:border-slate-700 dark:bg-slate-800",
                asset.thumbnail && "border-none"
              )}>
                {asset.thumbnail ? (
                  <img 
                    src={asset.thumbnail} 
                    alt={asset.assetCode} 
                    className="w-full h-full object-contain bg-black"
                  />
                ) : (
                  <div className="text-center p-8">
                    <ImageIcon className="mx-auto mb-2 h-12 w-12 text-slate-300 dark:text-slate-600" />
                    <p className="text-sm font-medium text-slate-400 dark:text-slate-500">No thumbnail available</p>
                  </div>
                )}
                {!readOnly && onUpdate && (
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    <span className="text-white font-medium bg-black/50 px-4 py-2 rounded-lg backdrop-blur-sm">
                      Upload Thumbnail
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleThumbnailUpload}
                    />
                  </label>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Status</p>
                  {onUpdate && !readOnly ? (
                    <select
                      value={getBidStatusValue(asset)}
                      onChange={(e) => onUpdate(asset.id, "status", e.target.value)}
                      className={cn(
                        "w-full px-2 py-1 text-sm font-bold border rounded focus:ring-2 focus:ring-indigo-500 outline-none transition-colors",
                        getBidStatusValue(asset) === "Bidding" && "bg-purple-50 text-purple-700 border-purple-200",
                        getBidStatusValue(asset) === "Off" && "bg-slate-50 text-slate-600 border-slate-200",
                        getBidStatusValue(asset) === "Lost" && "bg-rose-50 text-rose-700 border-rose-200",
                        getBidStatusValue(asset) === "Awarded" && "bg-emerald-50 text-emerald-700 border-emerald-200",
                        getBidStatusValue(asset) === "Rebid" && "bg-orange-50 text-orange-700 border-orange-200"
                      )}
                    >
                      {BID_ITEM_STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  ) : (
                    <p className={cn(
                      "text-sm font-bold",
                      getBidStatusValue(asset) === "Bidding" && "text-purple-600",
                      getBidStatusValue(asset) === "Off" && "text-slate-500",
                      getBidStatusValue(asset) === "Lost" && "text-slate-400",
                      getBidStatusValue(asset) === "Awarded" && "text-emerald-600",
                      getBidStatusValue(asset) === "Rebid" && "text-orange-600"
                    )}>
                      {getBidStatusValue(asset)}
                    </p>
                  )}
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Type</p>
                  {onUpdate && !readOnly ? (
                    <select
                      value={asset.type || ""}
                      onChange={(e) => onUpdate(asset.id, "type", e.target.value)}
                      className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    >
                      <option value="">Select Type...</option>
                      <option value="character">character</option>
                      <option value="creature">creature</option>
                      <option value="crowd">crowd</option>
                      <option value="environment">environment</option>
                      <option value="fx">fx</option>
                      <option value="dmp">dmp</option>
                      <option value="prop">prop</option>
                      <option value="vehicle">vehicle</option>
                      <option value="groom">groom</option>
                      <option value="contactSheet">contactSheet</option>
                      <option value="utility">utility</option>
                    </select>
                  ) : (
                    <p className="text-sm font-bold capitalize text-slate-800 dark:text-slate-100">{asset.type || "Not Set"}</p>
                  )}
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Quantity</p>
                  {onUpdate && !readOnly ? (
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={asset.quantity || 1}
                      onChange={(e) => onUpdate(asset.id, "quantity", parseInt(e.target.value) || 1)}
                      className="w-full rounded border border-slate-200 bg-white px-2 py-1 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                  ) : (
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{asset.quantity || 1}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="space-y-6">
              <div>
                <h3 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Description</h3>
                {onUpdate && !readOnly ? (
                  <textarea
                    value={asset.description || ""}
                    onChange={(e) => onUpdate(asset.id, "description", e.target.value)}
                    onKeyDown={(e) => handleTextareaKeyDown(e, "description", asset.description || "")}
                    className="min-h-[100px] w-full resize-y rounded-xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    placeholder="No description provided"
                  />
                ) : (
                  <div className="min-h-[100px] whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-200">
                    {asset.description || <span className="italic text-slate-300 dark:text-slate-500">No description provided</span>}
                  </div>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Scope of Work (Client Description)</h3>
                {onUpdate && !readOnly ? (
                  <textarea
                    value={asset.scopeOfWork || ""}
                    onChange={(e) => onUpdate(asset.id, "scopeOfWork", e.target.value)}
                    onKeyDown={(e) => handleTextareaKeyDown(e, "scopeOfWork", asset.scopeOfWork || "")}
                    className="min-h-[100px] w-full resize-y rounded-xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    placeholder="No scope of work provided"
                  />
                ) : (
                  <div className="min-h-[100px] whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-200">
                    {asset.scopeOfWork || <span className="italic text-slate-300 dark:text-slate-500">No scope of work provided</span>}
                  </div>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Assumptions</h3>
                {onUpdate && !readOnly ? (
                  <textarea
                    value={asset.assumptions || ""}
                    onChange={(e) => onUpdate(asset.id, "assumptions", e.target.value)}
                    onKeyDown={(e) => handleTextareaKeyDown(e, "assumptions", asset.assumptions || "")}
                    className="min-h-[80px] w-full resize-y rounded-xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                    placeholder="No assumptions provided"
                  />
                ) : (
                  <div className="min-h-[80px] whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm leading-relaxed text-slate-600 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-300">
                    {asset.assumptions || <span className="italic text-slate-300 dark:text-slate-500">No assumptions provided</span>}
                  </div>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Internal Notes</h3>
                {onUpdate && !readOnly ? (
                  <textarea
                    value={asset.internalNotes || ""}
                    onChange={(e) => onUpdate(asset.id, "internalNotes", e.target.value)}
                    onKeyDown={(e) => handleTextareaKeyDown(e, "internalNotes", asset.internalNotes || "")}
                    className="min-h-[80px] w-full resize-y rounded-xl border border-slate-200 bg-white p-4 text-sm italic leading-relaxed text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                    placeholder="No internal notes"
                  />
                ) : (
                  <div className="min-h-[80px] whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm italic leading-relaxed text-slate-600 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-300">
                    {asset.internalNotes || <span className="italic text-slate-300 dark:text-slate-500">No internal notes</span>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Task Days Summary */}
          <div className="mt-8 border-t border-slate-100 pt-8 dark:border-slate-800">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Task Allocation (Days)</h3>
            <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
              {[
                { key: "supervisor", label: "Supervision" },
                { key: "concept", label: "Concept" },
                { key: "dmp", label: "DMP" },
                { key: "model", label: "Model" },
                { key: "rig", label: "Rig" },
                { key: "retop", label: "Retop" },
                { key: "cloth", label: "Cloth" },
                { key: "fur", label: "Fur" },
                { key: "groom", label: "Groom" },
                { key: "texture", label: "Texture" },
                { key: "anim", label: "Anim" },
                { key: "fx", label: "FX" },
                { key: "lookdev", label: "Lookdev" },
                { key: "photoscan", label: "Photoscan" },
              ].map((task) => {
                let displayValue: number | string = asset.days[task.key as keyof Asset['days']] || 0;
                let isReadOnly = readOnly;

                if (task.key === "supervisor") {
                  const otherDays = 
                    (asset.days.concept || 0) +
                    (asset.days.dmp || 0) +
                    (asset.days.model || 0) +
                    (asset.days.rig || 0) +
                    (asset.days.retop || 0) +
                    (asset.days.cloth || 0) +
                    (asset.days.fur || 0) +
                    (asset.days.groom || 0) +
                    (asset.days.texture || 0) +
                    (asset.days.anim || 0) +
                    (asset.days.fx || 0) +
                    (asset.days.lookdev || 0) +
                    (asset.days.photoscan || 0);
                  
                  displayValue = (otherDays * ((setup.supervisionPercentage || 0) / 100)).toFixed(2);
                  if (displayValue.endsWith(".00")) displayValue = displayValue.slice(0, -3);
                  isReadOnly = true;
                }

                return (
                  <div key={task.key}>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">{task.label}</label>
                    {onUpdate && !isReadOnly ? (
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={displayValue || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          onUpdate(asset.id, "days", { ...asset.days, [task.key]: val === "" ? 0 : parseFloat(val) || 0 });
                        }}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[10pt] outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      />
                    ) : (
                      <div className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[10pt] font-medium text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        {displayValue || 0}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end border-t border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60">
          <button
            onClick={onClose}
            className="inline-flex items-center rounded-md border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
